/**
 * ADK Event Parser
 *
 * Parses ADK's native event format and converts it to a normalized event structure
 * similar to AG-UI protocol events. This allows the frontend to handle ADK events
 * in a consistent way until the backend is configured to emit AG-UI-compliant events.
 *
 * ADK Format:
 * - Tool calls: content.parts[].functionResponse
 * - Messages: content.parts[].text
 * - Full accumulated messages in each chunk (not deltas)
 */

import { v4 as uuidv4 } from 'uuid';
import { ADK_AUTHORS, ADK_EVENT_TYPES } from './constants';

/**
 * Parse ADK raw data and return normalized events
 * @param {Object} rawData - Raw ADK event data from SSE
 * @param {Set} processedChunkIds - Set of already processed chunk IDs to avoid duplicates
 * @param {Object} context - Context object with lastProcessedInvocationId ref
 * @returns {Array<Object>} Array of normalized events
 */
export function parseAdkEvent(rawData, processedChunkIds = new Set(), context = {}) {
  const events = [];
  const data = rawData;
  
  // Initialize fallback message ID in context if not present (for cases where invocationId is missing)
  // This ensures all events without invocationId use the same fallback ID, keeping them grouped
  if (!context.fallbackMessageId && !data.invocationId) {
    context.fallbackMessageId = `msg-${uuidv4()}`;
  }
  
  // If we get an invocationId, clear the fallback so we use the real ID going forward
  if (data.invocationId && context.fallbackMessageId) {
    delete context.fallbackMessageId;
  }

  // Skip if already processed (duplicate detection)
  if (data.id || (data.invocationId && data.time)) {
    const chunkId = data.id || `${data.invocationId}-${data.time}`;
    if (processedChunkIds.has(chunkId)) {
      return events; // Return empty array for duplicates
    }
    processedChunkIds.add(chunkId);
  }

  // FIRST: Check for errors in the data
  // Errors can come in various formats: errorMessage, error, errorCode, or in text content
  // Also handle plain text error objects created by SSE processor
  if (data.error || data.errorMessage || data.errorCode) {
    const errorMessage = data.error || data.errorMessage || `Error: ${data.errorCode || 'Unknown error'}`;
    console.log('adkEventParser: Detected error in data:', errorMessage);
    events.push({
      type: ADK_EVENT_TYPES.ERROR,
      error: errorMessage,
      errorCode: data.errorCode || extractErrorCode(errorMessage),
      status: data.status,
      timestamp: data.timestamp || data.time || Date.now(),
      rawEvent: data
    });
    console.log('adkEventParser: Created ERROR event:', events[0]);
    return events; // Return error event immediately
  }

  // Also check if the entire data object is just a string error message
  if (typeof data === 'string' && isPlainTextErrorString(data)) {
    events.push({
      type: ADK_EVENT_TYPES.ERROR,
      error: data,
      errorCode: extractErrorCode(data),
      timestamp: Date.now(),
      rawEvent: { text: data }
    });
    return events;
  }

  // Check if text content contains error messages (like "Error while running agent")
  if (data.content && data.content.parts && Array.isArray(data.content.parts)) {
    for (const part of data.content.parts) {
      if (part.text && typeof part.text === 'string') {
        // Check for common error patterns
        if (
          part.text.includes('Error while running agent') ||
          part.text.includes('Error 429') ||
          part.text.includes('Resource exhausted') ||
          part.text.includes('RESOURCE_EXHAUSTED') ||
          part.text.match(/Error \d{3}/)
        ) {
          events.push({
            type: ADK_EVENT_TYPES.ERROR,
            error: part.text,
            errorCode: extractErrorCode(part.text),
            timestamp: data.time || Date.now(),
            rawEvent: data
          });
          return events; // Return error event immediately
        }
      }
    }
  }

  // ONLY process data when author is in the allowed list
  const author = data.author;
  
  // Authors that process text-only content
  const textOnlyAuthors = [
    ADK_AUTHORS.DATA_ROUTER,
    ADK_AUTHORS.DIRECT_ANSWER,
    ADK_AUTHORS.METADATA_ANSWER,
    ADK_AUTHORS.REMEDIATION_PROPOSER
  ];
  
  // Authors that can process structured data (functionResponse) and text
  const structuredDataAuthors = [
    ADK_AUTHORS.PRESENTER,
    ADK_AUTHORS.REMEDIATION_CONFIRMATION,
    ADK_AUTHORS.REMEDIATION_SUMMARY
  ];
  
  // All allowed authors
  const allowedAuthors = [...textOnlyAuthors, ...structuredDataAuthors];
  
  // Only proceed if author is in the allowed list
  if (allowedAuthors.includes(author)) {
    // Variables to track structured data
    let extractedComponents = null;
    let extractedVulnerabilities = null;
    let extractedText = null;
    let expectedDataType = null; // Track what type of data we're expecting from functionResponse

    // Handle ADK format: content.parts[] with functionResponse, and text
    if (data.content && data.content.parts && Array.isArray(data.content.parts)) {
      
      // Process based on author type
      if (textOnlyAuthors.includes(author)) {
        // For text-only authors: only look for parts -> text
        for (const part of data.content.parts) {
          if (part.text) {
            extractedText = (extractedText || '') + part.text;
          }
        }
      } else if (structuredDataAuthors.includes(author)) {
        // For presenter and remediation_confirmation: look for functionResponse with items, and also content parts text
        
        // Extract data from functionResponse.response.items (new structure)
        for (const part of data.content.parts) {
          if (part?.functionResponse) {
            const functionResponse = part.functionResponse;
            const functionName = functionResponse?.name;
            
            // Check if functionResponse has response.items (new structure)
            const items = functionResponse?.response?.items;
            
            // Also check for direct items (backward compatibility)
            const directItems = functionResponse?.items;
            const finalItems = items || directItems;
            
            // Check function name to determine type
            if (functionName === 'present_components' || functionName === 'presentComponents') {
              expectedDataType = 'components';
            } else if (functionName === 'present_vulnerabilities' || functionName === 'presentVulnerabilities') {
              expectedDataType = 'vulnerabilities';
            }
            
            // Check if items is a string indicating the type ("vulnerabilities" or "components")
            if (finalItems === 'vulnerabilities') {
              expectedDataType = 'vulnerabilities';
            } else if (finalItems === 'components') {
              expectedDataType = 'components';
            }
            // Check if items is an array of actual data
            else if (Array.isArray(finalItems) && finalItems.length > 0) {
              // Determine type based on first item structure
              const firstItem = finalItems[0];
              if (firstItem.vulnerabilityName || firstItem['Vulnerability Name'] || firstItem.cve) {
                extractedVulnerabilities = finalItems;
              } else if (firstItem.name && firstItem.type) {
                extractedComponents = finalItems;
              }
            }
          }
          
          // Extract text content from parts
          if (part.text) {
            extractedText = (extractedText || '') + part.text;
          }
        }
      }

      // Create message events based on extracted data
      // For presenter: create separate events - first for structured data (table), then for text
      // extractedText will be a string if text was found, or null if no text was found
      const hasText = extractedText !== null && typeof extractedText === 'string' && extractedText.trim().length > 0;
      const hasStructuredData = extractedComponents || extractedVulnerabilities;
      
      // Check if this is a new message
      const lastInvocationId = context.lastProcessedInvocationId?.current;
      const isNewMessage = data.invocationId && (!lastInvocationId || data.invocationId !== lastInvocationId);

      // First, create event for structured data (table) if it exists
      if (hasStructuredData) {
        // Include author in messageId to prevent different authors from merging into same message
        const authorSuffix = author ? `-${author}` : '';
        const structuredMessageId = `${data.invocationId || context.fallbackMessageId || `msg-${uuidv4()}`}${authorSuffix}`;
        
        // Always create a new message start for structured data to ensure it's a separate message item
        // This prevents different authors from merging into the same message
        events.push({
          type: ADK_EVENT_TYPES.TEXT_MESSAGE_START,
          messageId: structuredMessageId,
          role: 'assistant',
          timestamp: data.time || Date.now(),
          rawEvent: data
        });
        
        if (isNewMessage && context.lastProcessedInvocationId) {
          context.lastProcessedInvocationId.current = data.invocationId;
        }

        events.push({
          type: ADK_EVENT_TYPES.TEXT_MESSAGE_CONTENT,
          messageId: structuredMessageId, // Include messageId to match with the correct message
          content: '', // No text in this event, only structured data
          components: extractedComponents,
          vulnerabilities: extractedVulnerabilities,
          isFullContent: true,
          timestamp: data.time || Date.now(),
          rawEvent: data
        });
      }

      // Then, create a separate event for text content if it exists
      // Use a different messageId to ensure it's a separate message item
      if (hasText) {
        // Always create a new message start for text to ensure it's a separate message item
        // Include author in messageId to prevent different authors from merging into same message
        const authorSuffix = author ? `-${author}` : '';
        const textMessageId = `${data.invocationId || context.fallbackMessageId || `msg-${uuidv4()}`}${authorSuffix}-text`;
        events.push({
          type: ADK_EVENT_TYPES.TEXT_MESSAGE_START,
          messageId: textMessageId,
          role: 'assistant',
          timestamp: data.time || Date.now(),
          rawEvent: data
        });

        events.push({
          type: ADK_EVENT_TYPES.TEXT_MESSAGE_CONTENT,
          messageId: textMessageId, // Include messageId to match with the correct message
          content: extractedText,
          components: null, // No structured data in this event, only text
          vulnerabilities: null,
          isFullContent: true,
          timestamp: data.time || Date.now(),
          rawEvent: data
        });
      }
    }
  }


  // Handle tool calls at root level (alternative ADK format)
  // ONLY process if author is in the allowed list
  if (allowedAuthors.includes(author) && 
      (data.toolCallId || data.tool_call_id || data.toolCall || data.tool_call || data.toolCalls || data.tool_calls)) {
    const toolCallId = data.toolCallId || data.tool_call_id || data.id || `tool-${Date.now()}-${Math.random()}`;
    const toolCallName = data.toolCallName || data.tool_call_name || data.name || data.toolName || 'Tool';

    // Handle tool call result
    if (
      data.result !== undefined ||
      data.toolCallResult ||
      data.tool_call_result ||
      (data.content && typeof data.content === 'string' && !data.content.parts)
    ) {
      const result = data.result || data.toolCallResult || data.tool_call_result || data.content || '';
      events.push({
        type: ADK_EVENT_TYPES.TOOL_CALL_RESULT,
        toolCallId,
        toolCallName,
        result,
        timestamp: data.time || Date.now(),
        rawEvent: data
      });

      events.push({
        type: ADK_EVENT_TYPES.TOOL_CALL_END,
        toolCallId,
        timestamp: data.time || Date.now(),
        rawEvent: data
      });
    }
    // Handle tool call with arguments
    else if (data.args || data.arguments || data.parameters || data.input) {
      const args = data.args || data.arguments || data.parameters || data.input;
      events.push({
        type: ADK_EVENT_TYPES.TOOL_CALL_START,
        toolCallId,
        toolCallName,
        args: typeof args === 'string' ? args : args,
        timestamp: data.time || Date.now(),
        rawEvent: data
      });
    }
    // Handle tool call start (just name/id)
    else {
      events.push({
        type: ADK_EVENT_TYPES.TOOL_CALL_START,
        toolCallId,
        toolCallName,
        args: {},
        timestamp: data.time || Date.now(),
        rawEvent: data
      });
    }
  }

  // Handle message content in various formats (fallback)
  // ONLY process if author is in the allowed list
  if (allowedAuthors.includes(author) && 
      !events.length && (data.content || data.text || data.message || data.delta)) {
    let content = '';

    if (data.content && typeof data.content === 'string') {
      content = data.content;
    } else if (data.text) {
      content = data.text;
    } else if (data.message) {
      content = typeof data.message === 'string' ? data.message : data.message.content || '';
    } else if (data.delta) {
      content = typeof data.delta === 'string' ? data.delta : data.delta.content || data.delta.text || '';
    }

    if (content) {
      // Check if this is a new message
      const lastInvocationId = context.lastProcessedInvocationId?.current;
      const isNewMessage = data.invocationId && (!lastInvocationId || data.invocationId !== lastInvocationId);

      if (isNewMessage) {
        // Include author in messageId to prevent different authors from merging into same message
        const authorSuffix = author ? `-${author}` : '';
        const fallbackMessageId = `${data.invocationId || context.fallbackMessageId || `msg-${uuidv4()}`}${authorSuffix}`;
        events.push({
          type: ADK_EVENT_TYPES.TEXT_MESSAGE_START,
          messageId: fallbackMessageId,
          role: 'assistant',
          timestamp: data.time || Date.now(),
          rawEvent: data
        });
        if (context.lastProcessedInvocationId) {
          context.lastProcessedInvocationId.current = data.invocationId;
        }
      }

      events.push({
        type: ADK_EVENT_TYPES.TEXT_MESSAGE_CONTENT,
        messageId: fallbackMessageId, // Include messageId to match with the correct message
        content,
        isFullContent: false, // Assume delta for fallback formats
        timestamp: data.time || Date.now(),
        rawEvent: data
      });
    }
  }

  // Handle turn completion
  if (data.turnComplete === true || data.partial === false) {
    events.push({
      type: ADK_EVENT_TYPES.TEXT_MESSAGE_END,
      timestamp: data.time || Date.now(),
      rawEvent: data
    });

    events.push({
      type: ADK_EVENT_TYPES.RUN_FINISHED,
      timestamp: data.time || Date.now(),
      rawEvent: data
    });
  }

  // If no events were created, return unknown event
  if (!events.length) {
    events.push({
      type: ADK_EVENT_TYPES.UNKNOWN,
      rawEvent: data,
      timestamp: data.time || Date.now()
    });
  }

  return events;
}

/**
 * Check if data is in ADK format (not AG-UI format)
 * @param {Object} data - Raw event data
 * @returns {boolean} True if data appears to be ADK format
 */
export function isAdkFormat(data) {
  // ADK format indicators:
  // - Has content.parts array
  // - Has functionResponse in parts
  // - Has invocationId
  // - Has actions.stateDelta
  // - No 'type' field matching AG-UI EventType enum

  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check for ADK-specific structure
  if (data.content && data.content.parts && Array.isArray(data.content.parts)) {
    return true;
  }

  if (data.invocationId) {
    return true;
  }

  if (data.actions && data.actions.stateDelta) {
    return true;
  }

  // Check for ADK tool call format
  if (
    data.toolCallId ||
    data.tool_call_id ||
    (data.content && data.content.parts && data.content.parts.some(p => p?.functionResponse))
  ) {
    return true;
  }

  return false;
}

/**
 * Check if a string is a plain text error message
 * @param {string} text - Text to check
 * @returns {boolean} True if text appears to be an error message
 */

// TODO: This is a temporary function to check errors and display them in the chat for testing purposes.
// We should get consensus if we want to keep this function or not.
function isPlainTextErrorString(text) {
  if (!text || typeof text !== 'string') return false;
  
  const errorPatterns = [
    /^Error while running agent:/i,
    /^Error \d{3}/i,
    /Resource exhausted/i,
    /RESOURCE_EXHAUSTED/i,
    /failed to call model:/i
  ];
  
  return errorPatterns.some(pattern => pattern.test(text));
}

/**
 * Extract error code from error message
 * @param {string} errorMessage - Error message text
 * @returns {string|null} Error code if found, null otherwise
 */
function extractErrorCode(errorMessage) {
  if (!errorMessage) return null;

  // Extract 429 from "Error 429"
  const error429Match = errorMessage.match(/Error (\d{3})/i);
  if (error429Match) {
    return error429Match[1];
  }

  // Check for RESOURCE_EXHAUSTED
  if (errorMessage.includes('RESOURCE_EXHAUSTED') || errorMessage.includes('Resource exhausted')) {
    return '429';
  }

  // Extract status codes
  const statusMatch = errorMessage.match(/Status:\s*(\w+)/i);
  if (statusMatch) {
    return statusMatch[1];
  }

  return null;
}
